import React, { useState, useEffect } from "react";
import { Box, Flex, Heading, Progress, Text, Button, Tooltip } from "@chakra-ui/react";
import { ArrowBackIcon, ArrowForwardIcon, CheckIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import QuestionnaireManager, { Answer } from "../../../questionnaire/questionnaire-manager";
import { SurveyButton } from "./SurveyButton";
import { isMobile } from "react-device-detect";

export interface QuestionData {
  Answers: string[];
  Is360Degree: boolean;
  Title: string;
  Subtitle: string | null;
  Id: string;
  Type?: string;
}

export interface QuestionDataResponse {
  Questions: QuestionData[];
  TeamMembers: string[];
  CandidateName: string;
}

interface SurveyBodyProps {
  onSurveyFinished: () => Promise<void>;
}


function timeout(delay: number) {
  return new Promise((res) => setTimeout(res, delay));
}

function SurveyBody(props: SurveyBodyProps) {
  const [selectedSurveyButtonId, setSelectedSurveyButtonId] = useState("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [sjtMost, setSjtMost] = useState<number | undefined>(undefined);
  const [sjtLeast, setSjtLeast] = useState<number | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function onClickCallback(id: string) {
    const proceed = selectedSurveyButtonId === "";
    QuestionnaireManager.SetAnswerForQuestionIndex(
      currentQuestionIndex,
      Number.parseInt(id.replace("survey-button-", ""))
    );
    setSelectedSurveyButtonId(id);
    await timeout(400);
    if (proceed && !QuestionnaireManager.IsLast()) getNextQuestion();
  }

  function isActive(id: string) {
    return id === selectedSurveyButtonId;
  }

  function getNextQuestion() {
    setDirection(1);
    setSelectedSurveyButtonId("");
    setCurrentQuestionIndex((prev) => prev + 1);
  }

  function getPreviousQuestion() {
    setDirection(-1);
    QuestionnaireManager.GoBackHappened();
    setSelectedSurveyButtonId("");
    setCurrentQuestionIndex((prev) => prev - 1);
  }

  useEffect(() => {
    const question = QuestionnaireManager.GetQuestionByIndex(currentQuestionIndex);
    if (question.type === 'SJT') {
      const sjt = QuestionnaireManager.GetSjtAnswerForQuestionIndex(currentQuestionIndex);
      setSjtMost(sjt?.mostEffective);
      setSjtLeast(sjt?.leastEffective);
    } else {
      const answerId = QuestionnaireManager.GetAnswerForQuestionIndex(currentQuestionIndex);
      setSelectedSurveyButtonId(answerId !== undefined ? `survey-button-${answerId}` : "");
    }
  }, [currentQuestionIndex]);

  const question = QuestionnaireManager.GetQuestionByIndex(currentQuestionIndex);
  const buttons = question.buttonLabels.map((x, i) => ({ name: x, index: i }));
  const isSituational = question.type === "Situational";
  const isSjt = question.type === "SJT";

  const canGoBack = QuestionnaireManager.CanGoBack();
  const canGoForward = isSjt
    ? (sjtMost !== undefined && sjtLeast !== undefined)
    : selectedSurveyButtonId.length !== 0;
  const isLast = QuestionnaireManager.IsLast() && canGoForward;
  const progressValue = QuestionnaireManager.ProgressValue();
  const total = QuestionnaireManager.MaxQuestionsCount();
  const hideHeader = buttons.length === 2;
  const headerText = isSjt
    ? "Select the most and least effective response:"
    : isSituational
      ? "Choose the most appropriate response:"
      : "To what extent do you agree?";
  const paddedNumber = String(currentQuestionIndex + 1).padStart(2, "0");

  return (
    <Flex direction="column" gap={6} w="100%">

      {/* Progress row */}
      <Flex alignItems="center" gap={4}>
        <Progress
          flex={1}
          size="xs"
          borderRadius="full"
          value={progressValue}
          bg="whiteAlpha.100"
          colorScheme="teal"
        />
        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap" fontWeight="medium" minW="60px" textAlign="right">
          {currentQuestionIndex + 1} of {total}
        </Text>
      </Flex>

      {/* Question area */}
      <Box position="relative" minH={isMobile ? "160px" : "200px"} overflow="hidden">
        {/* Watermark number */}
        <Text
          position="absolute"
          bottom="-0.15em"
          right="-0.05em"
          fontSize={isMobile ? "110px" : "160px"}
          fontWeight="900"
          color="whiteAlpha.50"
          lineHeight={1}
          userSelect="none"
          zIndex={0}
          transition="opacity 0.3s"
        >
          {paddedNumber}
        </Text>

        <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: direction > 0 ? 40 : -40 }}
            animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            style={{ position: "relative", zIndex: 1 }}
          >
            {!hideHeader && (
              <Text
                color="teal.400"
                fontSize="xs"
                letterSpacing="widest"
                textTransform="uppercase"
                fontWeight="semibold"
                mb={5}
              >
                {headerText}
              </Text>
            )}

            {!question.subtitle ? (
              <Heading
                size={isMobile ? "md" : "xl"}
                color="white"
                fontWeight="600"
                lineHeight="shorter"
                mt={hideHeader ? 3 : 0}
              >
                {question.title}
              </Heading>
            ) : (
              <>
                <Text fontSize="md" color="gray.300" fontWeight="medium" mb={3}>
                  {question.subtitle}
                </Text>
                <Text fontSize="xl" color="teal.200" fontStyle="italic" lineHeight="short">
                  "{question.title}"
                </Text>
              </>
            )}
          </motion.div>
      </Box>

      {/* Answer buttons */}
      <motion.div
          key={`ans-${currentQuestionIndex}`}
          initial={{ opacity: 0, x: direction > 0 ? 40 : -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
        >
          {isSjt ? (
            <Flex direction="column" gap={4}>
              {(['Most Effective', 'Least Effective'] as const).map((label) => {
                const isMostSection = label === 'Most Effective';
                const selected = isMostSection ? sjtMost : sjtLeast;
                const otherSelected = isMostSection ? sjtLeast : sjtMost;
                const setSelected = isMostSection
                  ? (i: number) => { setSjtMost(i); }
                  : (i: number) => { setSjtLeast(i); };
                const LETTERS = ["A", "B", "C", "D"];
                return (
                  <Box key={label}>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color={isMostSection ? "teal.400" : "orange.400"}
                      letterSpacing="widest"
                      textTransform="uppercase"
                      mb={2}
                    >
                      {label}
                    </Text>
                    <Flex direction="column" gap={2}>
                      {buttons.map((button) => {
                        const active = selected === button.index;
                        const disabled = otherSelected === button.index;
                        return (
                          <Flex
                            key={button.index}
                            onClick={() => { if (!disabled) setSelected(button.index); }}
                            align="center"
                            gap={3}
                            px={4}
                            py={3}
                            cursor={disabled ? "not-allowed" : "pointer"}
                            border="1px solid"
                            borderColor={active ? (isMostSection ? "teal.500" : "orange.400") : "whiteAlpha.200"}
                            borderRadius="lg"
                            bg={active ? (isMostSection ? "rgba(56,178,172,0.08)" : "rgba(251,146,60,0.08)") : "whiteAlpha.50"}
                            opacity={disabled ? 0.35 : 1}
                            transition="all 0.18s ease"
                            _hover={disabled ? {} : { borderColor: isMostSection ? "teal.400" : "orange.300", bg: active ? undefined : "whiteAlpha.100" }}
                            role="button"
                            tabIndex={disabled ? -1 : 0}
                            onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " ")) setSelected(button.index); }}
                          >
                            <Flex
                              w="28px"
                              h="28px"
                              borderRadius="md"
                              bg={active ? (isMostSection ? "teal.500" : "orange.400") : "whiteAlpha.100"}
                              border="1px solid"
                              borderColor={active ? (isMostSection ? "teal.400" : "orange.300") : "whiteAlpha.300"}
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                              transition="all 0.18s ease"
                            >
                              <Text fontSize="xs" fontWeight="bold" color={active ? "white" : "gray.400"}>
                                {LETTERS[button.index]}
                              </Text>
                            </Flex>
                            <Text
                              fontSize={isMobile ? "sm" : "md"}
                              color={active ? "white" : "gray.300"}
                              fontWeight={active ? "medium" : "normal"}
                              transition="color 0.18s ease"
                              lineHeight="short"
                              flex={1}
                            >
                              {button.name}
                            </Text>
                            <Flex
                              w="18px"
                              h="18px"
                              borderRadius="full"
                              border="2px solid"
                              borderColor={active ? (isMostSection ? "teal.400" : "orange.400") : "whiteAlpha.200"}
                              bg={active ? (isMostSection ? "teal.400" : "orange.400") : "transparent"}
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                              transition="all 0.18s ease"
                            >
                              {active && <CheckIcon w={2} h={2} color="white" />}
                            </Flex>
                          </Flex>
                        );
                      })}
                    </Flex>
                  </Box>
                );
              })}
            </Flex>
          ) : isSituational ? (
            <Flex direction="column" gap={2}>
              {buttons.map((button) => {
                const id = `survey-button-${button.index}`;
                const LETTERS = ["A", "B", "C", "D"];
                const active = isActive(id);
                return (
                  <Flex
                    key={id}
                    onClick={() => onClickCallback(id)}
                    align="center"
                    gap={3}
                    px={4}
                    py={3}
                    cursor="pointer"
                    border="1px solid"
                    borderColor={active ? "teal.500" : "whiteAlpha.200"}
                    borderRadius="lg"
                    bg={active ? "rgba(56,178,172,0.08)" : "whiteAlpha.50"}
                    transition="all 0.18s ease"
                    _hover={{ borderColor: active ? "teal.400" : "whiteAlpha.300", bg: active ? "rgba(56,178,172,0.12)" : "whiteAlpha.100" }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClickCallback(id); }}
                  >
                    <Flex
                      w="28px"
                      h="28px"
                      borderRadius="md"
                      bg={active ? "teal.500" : "whiteAlpha.100"}
                      border="1px solid"
                      borderColor={active ? "teal.400" : "whiteAlpha.300"}
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      transition="all 0.18s ease"
                    >
                      <Text fontSize="xs" fontWeight="bold" color={active ? "white" : "gray.400"}>
                        {LETTERS[button.index]}
                      </Text>
                    </Flex>
                    <Text
                      fontSize={isMobile ? "sm" : "md"}
                      color={active ? "white" : "gray.300"}
                      fontWeight={active ? "medium" : "normal"}
                      transition="color 0.18s ease"
                      lineHeight="short"
                      flex={1}
                    >
                      {button.name}
                    </Text>
                    <Flex
                      w="18px"
                      h="18px"
                      borderRadius="full"
                      border="2px solid"
                      borderColor={active ? "teal.400" : "whiteAlpha.200"}
                      bg={active ? "teal.400" : "transparent"}
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      transition="all 0.18s ease"
                    >
                      {active && <CheckIcon w={2} h={2} color="white" />}
                    </Flex>
                  </Flex>
                );
              })}
            </Flex>
          ) : (
            <Flex
              gap={3}
              flexWrap="wrap"
              direction={isMobile && hideHeader ? "column" : "row"}
            >
              {buttons.map((button) => {
                const id = `survey-button-${button.index}`;
                return (
                  <SurveyButton
                    key={id}
                    id={id}
                    index={button.index}
                    title={button.name}
                    onClick={() => onClickCallback(id)}
                    isActive={isActive(id)}
                  />
                );
              })}
            </Flex>
          )}
        </motion.div>

      {/* Navigation */}
      <Flex justifyContent="space-between" alignItems="center" mt={2}>
        <Button
          onClick={() => { if (!canGoBack) return; getPreviousQuestion(); }}
          variant="ghost"
          leftIcon={<ArrowBackIcon />}
          size="sm"
          color={canGoBack ? "gray.300" : "gray.700"}
          cursor={canGoBack ? "pointer" : "default"}
          _hover={{ bg: canGoBack ? "whiteAlpha.100" : "transparent" }}
        >
          Previous
        </Button>

        <Tooltip label={canGoForward ? "" : "Select an answer to proceed"}>
          <Button
            onClick={async () => {
              if (!canGoForward || isSubmitting) return;
              if (isSjt && sjtMost !== undefined && sjtLeast !== undefined) {
                QuestionnaireManager.SetSjtAnswerForQuestionIndex(currentQuestionIndex, sjtMost, sjtLeast);
              }
              if (isLast) {
                setIsSubmitting(true);
                const timeoutPromise = new Promise<void>((resolve) => setTimeout(resolve, 20000));
                await Promise.race([props.onSurveyFinished(), timeoutPromise]);
                setIsSubmitting(false);
              } else {
                getNextQuestion();
              }
            }}
            colorScheme={isLast ? "green" : "teal"}
            rightIcon={isLast && isSubmitting ? undefined : isLast ? <CheckIcon /> : <ArrowForwardIcon />}
            isLoading={isSubmitting}
            loadingText="Submitting..."
            size="sm"
            px={6}
            opacity={canGoForward && !isSubmitting ? 1 : 0.3}
            _hover={{ bg: canGoForward && !isSubmitting ? undefined : "transparent" }}
          >
            {QuestionnaireManager.IsLast() ? "Finish" : "Next"}
          </Button>
        </Tooltip>
      </Flex>

      {/* Finish hint */}
      <Text
        style={{ visibility: isLast ? "visible" : "hidden" }}
        fontSize="xs"
        color="gray.500"
        textAlign="center"
        mt={-3}
      >
        Clicking 'Finish' will complete and submit your assessment.
      </Text>

    </Flex>
  );
}

export interface SurveyPageProps {
  questionData: QuestionDataResponse;
  postData: (answer: SurveyAnswer) => Promise<void>;
}

function SurveyPage(props: SurveyPageProps) {
  QuestionnaireManager.Initialize(props.questionData.Questions);

  return (
    <Flex
      width={isMobile ? "95%" : "70%"}
      maxWidth="780px"
      mt={isMobile ? "1rem" : "2rem"}
      mb={isMobile ? "2rem" : "5rem"}
      direction="column"
      alignSelf="center"
      justifyContent="center"
      flexGrow={1}
    >
      <SurveyBody
        onSurveyFinished={async () => {
          const answers = QuestionnaireManager.GetAnswers();
          const goBackCount = QuestionnaireManager.GetGoBackCount();
          const surveyAnswer: SurveyAnswer = {
            metaData: { isMobile, goBackCount },
            answers,
          };
          await props.postData(surveyAnswer);
        }}
      />
    </Flex>
  );
}

export interface SurveyAnswer {
  answers: Answer[];
  metaData: {
    goBackCount: number;
    isMobile: boolean;
  };
}

export default SurveyPage;
